import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { setUser, checkOnboardingStage, normalizeFeatureCodes } from '../features/user/userSlice';
import { handleRawFetch401, apiInterceptor, authenticatedFetch } from '../utils/apiInterceptor';

function AuthRedirector() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const userState = useSelector(state => state.user);
  const onboarding = userState?.onboarding;
  const userToken = userState?.token;
  const brand_under_review = onboarding?.status === 'under-review';

  useEffect(() => {
    // Safety check to ensure user state is initialized
    if (!userState) {
      console.log('AuthRedirector: userState not initialized');
      return;
    }
    
    const token = localStorage.getItem('token');
    const isLoginPage = location.pathname === '/' || location.pathname === '/login' || location.pathname === '/signup';
    const isPublicPaymentPage = location.pathname.startsWith('/pay/');
    const isPaymentResultPage = location.pathname.startsWith('/payment/success') || location.pathname.startsWith('/payment/failure');
    const isOnboardingPage = location.pathname === '/onboarding';
    const isSupportTicketPage =
      location.pathname === '/support' || location.pathname.startsWith('/support/');
    
    console.log('AuthRedirector: Current path:', location.pathname);
    console.log('AuthRedirector: User token (Redux):', userToken);
    console.log('AuthRedirector: User token (localStorage):', token);
    console.log('AuthRedirector: Onboarding state:', onboarding);
    console.log('AuthRedirector: Is support ticket page:', isSupportTicketPage);
    // Prevent users with brand_under_review from accessing /subscriptions
    if (
      brand_under_review === true &&
      location.pathname === '/subscriptions'
    ) {
      console.log('AuthRedirector: Brand under review, redirecting from /subscriptions to /profile');
      navigate('/profile', { replace: true });
      return;
    }



    // If user is authenticated (has token in Redux or localStorage) and on login page, redirect to profile
    if (isLoginPage && (userToken || token)) {
      console.log('AuthRedirector: User authenticated on login page, processing redirect...');
      // If we have token in localStorage but not in Redux, try to restore user data
      if (!userToken && token) {
        console.log('AuthRedirector: Restoring user data from localStorage...');
        authenticatedFetch(process.env.REACT_APP_API_BASE_URL + '/api/auth/users/', {
          method: 'GET',
        })
          .then(res => {
            if (res.status === 401) {
              console.log('AuthRedirector: Token validation failed with 401 - triggering popup');
              handleRawFetch401();
              return null;
            }
            return res.ok ? res.json() : null;
          })
          .then(data => {
            if (data && data.user) {
              // Transform the API response to match what Redux expects
              // NEW API FORMAT: data.user_type, data.user.accessible_features, data.user.onboarding_stage
              const normalizedFeatures = normalizeFeatureCodes(data.user.accessible_features || []);

              const transformedData = {
                token: localStorage.getItem('token'),
                refresh: localStorage.getItem('refresh'),
                user_id: data.user.id,
                user_type: data.user_type,
                features: normalizedFeatures,
                brand_onboarding_stage: data.user.onboarding_stage
              };
              
              // Save to localStorage
              localStorage.setItem('user_id', transformedData.user_id);
              localStorage.setItem('user_type', transformedData.user_type);
              localStorage.setItem('features', JSON.stringify(normalizedFeatures));
              if (transformedData.brand_onboarding_stage !== undefined && transformedData.brand_onboarding_stage !== null) {
                localStorage.setItem('brand_onboarding_stage', transformedData.brand_onboarding_stage);
              }
              
              // Update Redux user state with transformed data
              dispatch(setUser(transformedData));
              console.log('AuthRedirector: User data restored, redirecting to profile');
              navigate('/profile', { replace: true });
            } else {
              // Invalid token, clear it using Redux logout action
              dispatch(setUser({
                token: null,
                refresh: null,
                user_id: null,
                user_type: null,
                features: [],
                brand_onboarding_stage: null,
              }));
              localStorage.removeItem('token');
              localStorage.removeItem('refresh');
              localStorage.removeItem('user_id');
              localStorage.removeItem('user_type');
              localStorage.removeItem('features');
              localStorage.removeItem('brand_onboarding_stage');
            }
          })
          .catch(err => {
            console.error('Auth check failed:', err);
            // Clear invalid token using Redux logout action
            dispatch(setUser({
              token: null,
              refresh: null,
              user_id: null,
              user_type: null,
              features: [],
              brand_onboarding_stage: null,
            }));
            localStorage.removeItem('token');
            localStorage.removeItem('refresh');
            localStorage.removeItem('user_id');
            localStorage.removeItem('user_type');
            localStorage.removeItem('features');
            localStorage.removeItem('brand_onboarding_stage');
          });
      } else {
        // After login, check onboarding stage only for appuser (brand users)
        if (userState?.user_type === 'USER') {
          console.log('AuthRedirector: User token exists in Redux, checking onboarding stage...');
          dispatch(checkOnboardingStage()).then((res) => {
            console.log('AuthRedirector: Onboarding stage response:', res);
            if (res.meta?.requestStatus === 'rejected') {
              console.warn('AuthRedirector: Onboarding stage check failed:', res.payload || res.error);
              if (res.payload?.shouldLogout || res.payload?.status === 401 || res.payload?.status === 403) {
                navigate('/login', { replace: true, state: { sessionExpired: true } });
                return;
              }
              navigate('/onboarding', { replace: true });
              return;
            }
            const stage = res.payload?.stage;
            const status = res.payload?.status;
            if (stage === 'pending' || stage === 'draft') {
              console.log('AuthRedirector: Redirecting to onboarding, stage:', stage);
              navigate('/onboarding', { replace: true });
            } else if (stage === 'completed') {
              if (status === 'under-review') {
                console.log('AuthRedirector: Brand under review, staying on onboarding for welcome screen');
                navigate('/onboarding', { replace: true });
              } else {
                console.log('AuthRedirector: Redirecting to growth-suite, onboarding complete');
                navigate('/growth-suite', { replace: true });
              }
            } else {
              console.log('AuthRedirector: Unknown stage, redirecting to onboarding by default:', stage);
              navigate('/onboarding', { replace: true });
            }
          }).catch((error) => {
            console.error('AuthRedirector: Error checking onboarding stage:', error);
            // Default to onboarding if check fails
            navigate('/onboarding', { replace: true });
          });
        } else {
          // For employee/admin, go to growth-suite
          navigate('/growth-suite', { replace: true });
        }
      }
    }
    
    // If user is not authenticated and trying to access protected pages, redirect to login
    if (!isLoginPage && !isPublicPaymentPage && !isPaymentResultPage && !userToken && !token) {
      navigate('/login', { replace: true });
    }
    // If user is authenticated and tries to access main app while onboarding not complete, redirect
    // Exclude support ticket pages from this redirect
    if (!isLoginPage && !isOnboardingPage && !isSupportTicketPage && userToken && onboarding && onboarding.stage && onboarding.stage !== 'completed') {
      console.log('AuthRedirector: User has not completed onboarding, redirecting to onboarding');
      navigate('/onboarding', { replace: true });
    } else if (isSupportTicketPage && userToken && onboarding && onboarding.stage && onboarding.stage !== 'completed') {
      console.log('AuthRedirector: User is accessing support ticket page, allowing access despite incomplete onboarding');
    }
  }, [location, userToken, navigate, dispatch, userState, onboarding, brand_under_review]);

  return null;
}

export default AuthRedirector;
